import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PhoneOutgoing, Search, Lock } from "lucide-react";

// The employee never sees the real number — only a masked one. Calls are
// initiated by passing task_id / call_id so the server resolves the real
// phone against the owner's DB.
export default function EmpCustomers() {
  const [tasks, setTasks] = useState([]);
  const [calls, setCalls] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [t, c] = await Promise.all([api.get("/tasks"), api.get("/calls")]);
        setTasks(t.data);
        setCalls(c.data);
      } catch {
        /* ignore — auth interceptor handles 401 */
      }
    })();
  }, []);

  const customers = useMemo(() => {
    const map = new Map();
    const add = (name, phone, task_id, call_id) => {
      const label = (name || "Unknown").trim() || "Unknown";
      const ph = phone || "";
      const k = `${label}|${ph}`;
      if (!map.has(k)) {
        map.set(k, {
          key: k,
          name: label,
          phone_masked: ph,
          task_id: task_id || null,
          call_id: call_id || null,
        });
      }
    };
    tasks.forEach((t) => add(t.customer_name, t.customer_phone, t.id, null));
    calls.forEach((c) => add(c.customer_name, c.customer_phone, null, c.id));
    return Array.from(map.values());
  }, [tasks, calls]);

  const filtered = customers.filter((c) => {
    if (!q) return true;
    return (c.name || "").toLowerCase().includes(q.toLowerCase());
  });

  const call = async (c) => {
    try {
      const payload = { customer_name: c.name };
      if (c.task_id) payload.task_id = c.task_id;
      else if (c.call_id) payload.call_id = c.call_id;
      else {
        toast.error("No task reference for this customer.");
        return;
      }
      const { data } = await api.post("/calls/initiate", payload);
      toast.success(data.message || "Your phone will ring in a few seconds.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start call.");
    }
  };

  return (
    <div className="p-4 space-y-4" data-testid="emp-customers">
      <div>
        <div className="mono text-[10px] uppercase tracking-widest text-[#FF5000]">
          Directory
        </div>
        <h1 className="font-heading text-2xl font-bold mt-1">Customers</h1>
        <p className="mono text-[10px] text-slate-500 mt-1 flex items-center gap-1">
          <Lock className="w-3 h-3" /> Customer numbers are masked for privacy.
        </p>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name"
          className="pl-9"
          data-testid="emp-customers-search"
        />
      </div>

      <div className="space-y-2">
        {filtered.map((c) => (
          <Card
            key={c.key}
            className="border-slate-200 shadow-none"
            data-testid={`emp-customer-${c.key}`}
          >
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-700 grid place-items-center font-heading font-bold">
                {(c.name || "?").slice(0, 1)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{c.name}</div>
                <div className="mono text-[11px] text-slate-500 truncate">
                  {c.phone_masked}
                </div>
              </div>
              <Button
                size="icon"
                onClick={() => call(c)}
                className="bg-[#0055FF] hover:bg-[#0040CC] rounded-full w-10 h-10"
                data-testid={`emp-customer-call-${c.key}`}
              >
                <PhoneOutgoing className="w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div className="text-sm text-slate-500 text-center p-6">
            No customers found.
          </div>
        )}
      </div>
    </div>
  );
}
